import { post } from "../utils/Backend";
import { useH5Mock } from "../global/GlobalMock";
import {
  mockGetSpaceFavorState,
  mockGetSpaceHomeData,
  mockGetSpaceProfile,
  mockGetStartupConfig,
  mockGetSubscriptionDetail,
  mockSetSpaceFavor,
} from "./SpaceMock";
import type {
  GetHomeDataResult,
  GetSubscriptionDetailResult,
  HomeLatestViewpoint,
  HomeLiveBroadcast,
  SpaceFavorState,
  SpaceModuleConfig,
  SpaceProfile,
} from "./SpaceType";

function normalizeLive(
  live: HomeLiveBroadcast | null | undefined
): HomeLiveBroadcast | null {
  if (live == null) {
    return null;
  }
  return {
    coverUrl: live.coverUrl,
    liveId: live.liveId,
    title: live.title,
    phase: live.phase ?? "live",
    viewerCount: live.viewerCount ?? null,
    reserveCount: live.reserveCount ?? null,
  };
}

function normalizeViewpoint(
  vp: HomeLatestViewpoint | null | undefined
): HomeLatestViewpoint | null {
  if (vp == null) {
    return null;
  }
  return {
    imageUrl: vp.imageUrl,
    imageUrls: vp.imageUrls ?? null,
    content: vp.content,
    publishTime: vp.publishTime,
    contentAccessible: vp.contentAccessible ?? false,
    requiredProductDisplayName: vp.requiredProductDisplayName ?? null,
    spaceProductId: vp.spaceProductId ?? null,
    importance: vp.importance ?? null,
    categoryName: vp.categoryName ?? null,
    publisherLabel: vp.publisherLabel ?? null,
    publisherNickname: vp.publisherNickname ?? null,
    publisherAvatarUrl: vp.publisherAvatarUrl ?? null,
    title: vp.title ?? null,
    titleMode: vp.titleMode ?? null,
    likeCount: vp.likeCount ?? 0,
    collectCount: vp.collectCount ?? 0,
  };
}

const emptyHomeData = (): GetHomeDataResult => ({
  rotatingAds: [],
  live: null,
  latestViewpoint: null,
  courses: null,
});

export const SpaceApi = {
  async getSpaceProfile(spaceId: string): Promise<SpaceProfile | null> {
    if (useH5Mock) {
      return mockGetSpaceProfile(spaceId);
    }
    const res = await post<{ spaceId: string }, SpaceProfile>(
      "/lark/app/space/getPublicSpaceProfile",
      { spaceId },
      { silent: true }
    );
    if (typeof res === "string") {
      return null;
    }
    return res;
  },

  async getStartupConfig(spaceId: string): Promise<SpaceModuleConfig[]> {
    if (useH5Mock) {
      return mockGetStartupConfig(spaceId);
    }
    const res = await post<{ spaceId: string }, SpaceModuleConfig[]>(
      "/lark/app/space/getStartupConfig",
      { spaceId },
      { silent: true }
    );
    if (typeof res === "string" || !Array.isArray(res)) {
      return [];
    }
    return res;
  },

  async getSpaceHomeData(spaceId: string): Promise<GetHomeDataResult> {
    if (useH5Mock) {
      return mockGetSpaceHomeData(spaceId);
    }
    const res = await post<{ spaceId: string }, GetHomeDataResult>(
      "/lark/app/miniapp/home/getHomeData",
      { spaceId },
      { silent: true }
    );
    if (typeof res === "string" || res == null) {
      return emptyHomeData();
    }
    return {
      rotatingAds: res.rotatingAds ?? [],
      live: normalizeLive(res.live),
      latestViewpoint: normalizeViewpoint(res.latestViewpoint),
      courses: res.courses ?? null,
    };
  },

  async recordVideoTutorialPlay(
    spaceId: string,
    tutorialId: string
  ): Promise<number | null> {
    if (useH5Mock) {
      return null;
    }
    const res = await post<
      { spaceId: string; tutorialId: string },
      { learnerCount?: number | null }
    >(
      "/lark/app/miniapp/home/recordVideoTutorialPlay",
      { spaceId, tutorialId },
      { silent: true }
    );
    if (typeof res === "string" || res == null) {
      return null;
    }
    return res.learnerCount ?? null;
  },

  async getSubscriptionDetail(
    spaceId: string
  ): Promise<GetSubscriptionDetailResult | string> {
    if (useH5Mock) {
      return mockGetSubscriptionDetail(spaceId);
    }
    return post<{ spaceId: string }, GetSubscriptionDetailResult>(
      "/lark/app/user/getSubscriptionDetail",
      { spaceId },
      { silent: true }
    );
  },

  async getSpaceFavorState(spaceId: string): Promise<SpaceFavorState | null> {
    if (useH5Mock) {
      return mockGetSpaceFavorState();
    }
    const res = await post<{ spaceId: string }, SpaceFavorState>(
      "/lark/app/user/getSpaceFavorState",
      { spaceId },
      { silent: true }
    );
    if (typeof res === "string" || res == null) {
      return null;
    }
    return res;
  },

  async setSpaceFavor(spaceId: string, favor: boolean): Promise<boolean> {
    if (useH5Mock) {
      return mockSetSpaceFavor();
    }
    const res = await post<{ spaceId: string; favor: boolean }, boolean>(
      "/lark/app/user/setSpaceFavor",
      { spaceId, favor }
    );
    return typeof res !== "string";
  },
};
